/**
 * @bos/portal3 — "how my runs reach me", on the account page.
 *
 * A run that halts at an owner door, fails, or completes can now tell the owner who
 * walked away (the server's run notifier). This card edits the three per-account fields
 * that govern it — the webhook URL (reaches a phone via ntfy/Discord/Slack), the Windows
 * desktop toast toggle, and whether completions notify too. Both channels are OFF until
 * configured here; halts and failures always notify on whatever channels are on.
 *
 * Same shape as ModelChoice: load the server truth, edit locally, save the whole card at
 * once (`account.setNotifications` writes all three fields together — a partial write
 * would leave the row saying something nobody asked for).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { TRPCClientError } from '@trpc/client';
import { client } from '../rpc/client';

interface WireNotifySettings {
  readonly webhookUrl: string | null;
  readonly desktop: boolean;
  readonly onCompleted: boolean;
}

function errorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) return err.message || 'Something went wrong.';
  return 'Could not reach the server. Please try again.';
}

export function NotificationSettings() {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [desktop, setDesktop] = useState(false);
  const [onCompleted, setOnCompleted] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = (await client.account.notifications.query()) as WireNotifySettings;
      setWebhookUrl(r.webhookUrl ?? '');
      setDesktop(r.desktop);
      setOnCompleted(r.onCompleted);
      setLoaded(true);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (failed || !loaded) return null; // a read that failed says nothing rather than guessing

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await client.account.setNotifications.mutate({
        webhookUrl: webhookUrl.trim() === '' ? null : webhookUrl.trim(),
        desktop,
        onCompleted,
      });
      setSaved(true);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" data-testid="account-notifications">
      <h2 className="card__title">Run notifications</h2>
      <p className="card__intro">
        A run that stops and waits for you — a question, an approval, a failure — can tell
        you, instead of waiting silently for you to look.
      </p>

      <form
        className="form"
        onSubmit={save}
        aria-label="Run notifications"
        style={{ marginTop: '1.1rem' }}
      >
        {error !== null && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="form__success" role="status">
            Saved. Your runs notify these channels from now on.
          </p>
        )}

        <label className="form__field">
          <span className="form__label">Webhook URL</span>
          <input
            className="form__input"
            type="url"
            name="webhookUrl"
            placeholder="leave empty — no webhook"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            disabled={busy}
          />
        </label>
        <p className="form__hint">
          Each event is POSTed there as compact JSON — point it at ntfy.sh, a Discord or
          Slack webhook, or your own endpoint, and it reaches your phone.
        </p>

        <label className="form__field form__field--inline">
          <input
            type="checkbox"
            name="desktop"
            checked={desktop}
            onChange={(e) => setDesktop(e.target.checked)}
            disabled={busy}
          />
          <span className="form__label">Show a desktop popup on the server machine</span>
        </label>

        <label className="form__field form__field--inline">
          <input
            type="checkbox"
            name="onCompleted"
            checked={onCompleted}
            onChange={(e) => setOnCompleted(e.target.checked)}
            disabled={busy}
          />
          <span className="form__label">
            Also notify when a run completes (halts and failures always notify)
          </span>
        </label>

        <div className="form__actions">
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save notifications'}
          </button>
        </div>
      </form>
    </div>
  );
}
